import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/app/Shared";
import { api } from "@/lib/api";
import { GraduationCap } from "lucide-react";

export default function HaccpTraining() {
  const { user } = useAuth();
  const [restaurantName, setRestaurantName] = useState("");

  useEffect(() => {
    // Resolve the chef's primary restaurant name so it appears on the certificate.
    if (!user) return;
    if (user.restaurant_name) { setRestaurantName(user.restaurant_name); return; }
    const rid = user.restaurant_id || (user.restaurant_ids && user.restaurant_ids[0]);
    if (!rid) return;
    api.get("/restaurants").then((r) => {
      const match = (r.data || []).find((x) => x.id === rid);
      if (match?.name) setRestaurantName(match.name);
    }).catch(() => {});
  }, [user]);

  // Bridge: relay training progress from the iframe to the Stockly backend
  // so admin dashboards can track who has passed which HACCP levels.
  useEffect(() => {
    if (!user) return;
    const onMessage = async (e) => {
      const data = e?.data;
      if (!data || data.source !== "stockly-haccp") return;

      if (data.type === "haccp-progress" && data.kind === "real") {
        api.post("/training/haccp/progress", {
          levels_passed: Array.isArray(data.levels_passed) ? data.levels_passed : [],
          best_scores: data.best_scores || {},
          certified: !!data.certified,
          last_level: data.level ?? null,
          last_pct: data.pct ?? null,
        }).catch(() => {});
        return;
      }

      if (data.type === "haccp-cert-download") {
        try {
          const r = await api.post("/training/haccp/certificate", {
            png_b64: data.png_b64,
            action: "download_pdf",
            restaurant_name: data.restaurant_name || restaurantName,
          });
          const bin = atob(r.data.pdf_b64);
          const arr = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
          const blob = new Blob([arr], { type: "application/pdf" });
          const url = URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = r.data.filename || "certificaat.pdf";
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          toast.success("PDF gedownload");
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Downloaden mislukt");
        }
        return;
      }

      if (data.type === "haccp-cert-mail") {
        try {
          const r = await api.post("/training/haccp/certificate", {
            png_b64: data.png_b64,
            action: "email_admin",
            restaurant_name: data.restaurant_name || restaurantName,
          });
          const to = (r.data.sent_to || []).join(", ");
          toast.success(`Certificaat verzonden${to ? ` naar ${to}` : ""}`);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Versturen mislukt");
        }
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [user, restaurantName]);

  const src = useMemo(() => {
    if (!user) return null; // Wait for auth before mounting iframe so params are correct
    const params = new URLSearchParams();
    if (user.name) params.set("name", user.name);
    if (restaurantName) params.set("loc", restaurantName);
    const qs = params.toString();
    return `/haccp-app/index.html${qs ? `?${qs}` : ""}`;
  }, [user, restaurantName]);

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-[1600px] mx-auto" data-testid="haccp-training-page">
      <PageHeader
        overline="Training"
        title="HACCP Hygiënecode Horeca"
        description="Vijf niveaus, 1000 vragen, meerdere talen. Na alle vijf echte toetsen (≥70%) ontvang je een certificaat op naam."
        actions={
          <div className="hidden sm:flex items-center gap-2 h-9 px-3 border border-zinc-200 rounded-md text-xs font-semibold text-zinc-600">
            <GraduationCap size={14} className="text-[#5BC044]" />
            {user?.name || "Chef"}{restaurantName ? ` · ${restaurantName}` : ""}
          </div>
        }
      />
      <div className="card-flat overflow-hidden" data-testid="haccp-training-frame-wrap">
        {src ? (
          <iframe
            title="HACCP training"
            src={src}
            className="w-full"
            style={{ height: "calc(100vh - 200px)", minHeight: "700px", border: 0 }}
            data-testid="haccp-training-iframe"
          />
        ) : (
          <div className="p-8 text-sm text-zinc-500">Laden…</div>
        )}
      </div>
    </div>
  );
}
